import { useState } from "react"
import { api } from "../../lib/api"
import type { Compra, Produto } from "../../types"
import { prazo, reais } from "../../lib/format"
import { Card, Erro } from "../ui"
import Desempenho from "../Painel/Desempenho"
import BotaoCartao, { LinhaParcelada } from "./BotaoCartao"
import { FormularioPix } from "./index"

/**
 * A página de renovação: o que a pessoa construiu (progresso e onde focar,
 * por habilitação) e o preço de continuar. É o equivalente, para quem já foi
 * cliente, da página de resultado do diagnóstico — a narrativa de "comece" não
 * serve para quem tem histórico aqui, e mandar essa pessoa para o checkout
 * genérico, ao preço cheio, é o que acontecia antes.
 *
 * O Pix abre no lugar do botão, como na página de resultado. Os marcos do
 * funil são os de sempre: `view_item` já saiu na carga do /acesso,
 * `clique_pagar` é o clique que revela o CPF, `begin_checkout` fica no
 * servidor.
 */
export default function Renovacao({ produto, aoCriar }: { produto: Produto; aoCriar: (compra: Compra) => void }) {
  const [aberto, setAberto] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  function abrir() {
    setAberto(true)
    setErro(null)
    api.post<unknown>("/eventos", { nome: "clique_pagar", habilitacao: produto.slug, gateway: "asaas" }).catch(() => {})
  }

  const pilares: [string, string][] = [
    ["Seu histórico continua", "Evolução, notas e acerto por unidade seguem de onde pararam — nada zera."],
    ["Questões comentadas", "A resposta certa, o comentário e a norma citada quando houver (NORMAM, LESTA, RIPEAM)."],
    ["Simulados no formato da prova", "O mesmo número de questões, o mesmo tempo e a mesma nota de corte do exame da Capitania."],
    ["Saber quando está pronto", "Continue até passar com margem. Aí marque a prova sabendo que vai passar."],
  ]

  return (
    <div className="space-y-8">
      <Card className="revela text-center">
        <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">
          Renovação · {produto.pacote ? produto.nome : produto.habilitacao}
        </p>
        <h1 className="mt-2 text-2xl font-extrabold leading-tight text-navy-900 sm:text-3xl">
          Sua preparação de {produto.habilitacoes.length > 1 ? "Arrais-Amador" : produto.habilitacao} venceu.
        </h1>
        <p className="mt-2 text-navy-800">
          O que você construiu continua aqui. Renove por{" "}
          <strong className="font-semibold">{reais(produto.preco_centavos)}</strong> e ganhe mais{" "}
          {prazo(produto.meses_de_acesso)} para chegar pronto à prova.
        </p>
      </Card>

      {/* O boletim da habilitação, o mesmo do painel. Ele se esconde sozinho
          se não houver simulado no formato da prova. */}
      {/* O boletim da habilitação principal — no completo, o Arrais. */}
      <Desempenho slug={produto.habilitacoes[0] ?? produto.slug} nome={produto.habilitacoes.length > 1 ? "Arrais-Amador" : produto.habilitacao} />

      <section className="revela">
        <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">A renovação</p>
        <h2 className="mt-1 text-lg font-bold leading-snug text-navy-900">Siga de onde parou, até passar com margem</h2>

        <Card className="mt-4 border-gold-500 p-6 sm:p-7">
          <ul className="space-y-3">
            {pilares.map(([titulo, texto]) => (
              <li key={titulo} className="flex gap-3 text-sm">
                <span className="mt-0.5 font-bold text-gold-600" aria-hidden>
                  ✓
                </span>
                <span>
                  <strong className="font-semibold text-navy-900">{titulo}</strong>
                  <span className="block text-slate-600">{texto}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-6 border-t border-navy-50 pt-5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <p className="text-3xl font-extrabold tracking-tight text-navy-900">{reais(produto.preco_centavos)}</p>
              <p className="text-sm text-slate-600">renovação · mais {prazo(produto.meses_de_acesso)}</p>
            </div>
            <LinhaParcelada produto={produto} className="mt-1" />
            <p className="mt-1 text-sm font-semibold text-navy-900">Sem mensalidade. Sem renovação automática.</p>

            <div className="mt-5">
              {erro && <Erro mensagem={erro} />}
              {aberto ? (
                <div className={erro ? "mt-3" : ""}>
                  <FormularioPix layout="linha" produto={produto} aoCriar={aoCriar} aoFalhar={setErro} />
                  <p className="mt-2 max-w-[19rem] text-xs text-slate-500">O QR code aparece aqui.</p>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={abrir}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-sucesso-700 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-sucesso-800 sm:w-auto"
                  >
                    Renovar minha preparação
                  </button>
                  <BotaoCartao produto={produto} aoFalhar={setErro} className="w-full sm:w-auto" />
                </div>
              )}
            </div>

            <p className="mt-3 text-xs text-slate-500">
              Sete dias para desistir, com reembolso integral e sem justificar.
            </p>
          </div>
        </Card>
      </section>
    </div>
  )
}
